// Catena completa RAL -> stipendio netto, in una sola funzione pura.
//
// Questa e' la sola implementazione dell'orchestrazione: prima viveva in tre
// copie (computeFromParams in js/script.js, scripts/lib/calcolo-netto.js e una
// copia locale nei golden test). Chi calcola un netto - la UI del calcolatore,
// il solver inverso, i generatori di pagine, i test - passa da qui.
//
// Nessuna dipendenza dal DOM: `mensilita` va sempre passata esplicitamente,
// perche' employee.calculateRetribuzioneMensileNetta altrimenti la cerca in
// pagina. La sequenza e' congelata dai golden test (tests/netto-golden.test.js):
// se cambia qui, quei valori devono essere ricontrollati a mano.
import * as employee from './employee.js';

// I default replicano quelli del form: cuneo fiscale e bonus 100 attivi,
// aliquote INPS regolari, nessun familiare a carico.
export function computeNetto(data, params = {}) {
  const year = params.year || '2026';
  const mensilita = Number(params.mensilita) || 14;
  const ral = Number(params.ral) || 0;

  const alinpsDip = params.alinpsDip ?? data.aliquote_inps.lavoratore.regolare * 100;
  const alinpsDat = params.alinpsDat ?? data.aliquote_inps.azienda.regolare * 100;

  const massimale = data.aliquote_inps[`massimale${year}`] || data.aliquote_inps.massimale2026;
  const imponibileInpsBase = Math.min(ral, massimale);

  const { inpsDipendente, inpsAzienda } = employee.calculateInps(imponibileInpsBase, data, {
    year,
    esoneroContributivo: params.esoneroContributivo ?? false,
    madreLavoratrice: params.madreLavoratrice ?? false,
    mensilita,
    apprendistato: params.apprendistato ?? false,
    alinpsDip,
    alinpsDat,
  });

  const imponibileIrpef = employee.calculateImponibileIrpef(ral, inpsDipendente, {
    rientroCervelli: params.rientroCervelli ?? false,
    imponibileCervelli: params.imponibileCervelli ?? 1,
  });
  // Reddito complessivo al lordo dello sconto impatriati: e' la base delle
  // soglie di bonus 100, bonus cuneo e ulteriore detrazione, che per legge
  // includono per intero la quota esente. Coincide con l'imponibile negli
  // altri casi.
  const redditoComplessivo = ral - inpsDipendente;

  const irpefLorda = employee.calculateIrpef(imponibileIrpef, data, { year });

  const detrazioneLavoro = employee.calculateDetrazioniIrpef(imponibileIrpef, { ral });
  const detrazioneConiuge = employee.calculateDetrazioneConiuge(imponibileIrpef, {
    coniugeCarico: params.coniugeCarico ?? false,
  });
  const detrazioneFigli = employee.calculateDetrazioneFigli(imponibileIrpef, {
    numeroFigli: params.figliCarico === false ? 0 : (params.numeroFigli ?? 0),
    numeroFigliDisabili: params.numeroFigliDisabili ?? 0,
    percentualeFigli: params.percentualeFigli ?? 100,
  });
  const detrazioneAscendenti = employee.calculateDetrazioneAscendenti(imponibileIrpef, {
    // Spuntare "ascendenti a carico" senza indicarne il numero vale 1.
    numeroAscendenti: params.ascendenteCarico
      ? (params.numeroAscendenti || 1)
      : (params.ascendenteCarico === false ? 0 : (params.numeroAscendenti ?? 0)),
  });
  const ulterioreDetrazione = employee.calculateUlterioreDetrazione(ral, inpsDipendente, {
    cuneoFiscale: params.cuneoFiscale ?? true,
  });

  const irpefNetta = employee.calculateIrpefNetta(
    irpefLorda, detrazioneLavoro, detrazioneConiuge, ulterioreDetrazione,
    detrazioneFigli, detrazioneAscendenti,
  );

  const addizionaleRegionale = employee.calculateAddizionaleRegionale(imponibileIrpef, data, irpefNetta, {
    regione: params.regione || '',
    year,
  });
  const addizionaleComunale = employee.calculateAddizionaleComunale(imponibileIrpef, irpefNetta, {
    addizionaleComunale: params.addizionaleComunale ?? 0,
    esenzione: params.comuneEsenzione ?? 0,
    scaglioni: params.comuneScaglioni ?? null,
  });

  const bonus100 = employee.calculateBonus100(
    imponibileIrpef, detrazioneLavoro, irpefLorda, detrazioneConiuge, ulterioreDetrazione,
    {
      bonus100Flag: params.bonus100Flag ?? true,
      altreDetrazioni: params.altreDetrazioni ?? 0,
      detrazioneFigli,
      detrazioneAscendenti,
      redditoComplessivo,
    },
  );
  const sommaIntegrativa = employee.calculateSommaIntegrativa(imponibileIrpef, {
    cuneoFiscale: params.cuneoFiscale ?? true,
    redditoComplessivo,
  });

  const totaleIrpef = employee.calculateTotaleIrpef(irpefNetta, addizionaleComunale, addizionaleRegionale);
  const nettoAnnuo = employee.calculateRetribuzioneAnnuaNetta(ral, inpsDipendente, totaleIrpef, sommaIntegrativa, bonus100);
  const nettoMensileMedio = employee.calculateRetribuzioneMensileNetta(nettoAnnuo, { mensilita });

  const ripartizione = employee.calculateNettoMensilita(nettoAnnuo, {
    ral, inpsDipendente, irpefLorda, irpefNetta,
    addizionaleRegionale, addizionaleComunale, sommaIntegrativa, bonus100,
  }, { mensilita });

  const trattamentoFineRapporto = Math.round(ral / 13.5);
  const inail = employee.calculateInail(ral, data);
  const costoAzienda = Math.round(ral + trattamentoFineRapporto + inpsAzienda + inail);

  return {
    ral,
    mensilita,
    inpsDipendente,
    inpsAzienda,
    imponibileIrpef,
    irpefLorda,
    detrazioneLavoro,
    detrazioneConiuge,
    detrazioneFigli,
    detrazioneAscendenti,
    ulterioreDetrazione,
    irpefNetta,
    addizionaleRegionale,
    addizionaleComunale,
    bonus100,
    sommaIntegrativa,
    totaleIrpef,
    nettoAnnuo,
    nettoMensileMedio,
    // Con 12 mensilita' non esiste una mensilita' aggiuntiva: ordinaria = media.
    nettoMensilitaOrdinaria: ripartizione?.nettoMensilitaOrdinaria ?? nettoMensileMedio,
    nettoMensilitaAggiuntiva: ripartizione?.nettoMensilitaAggiuntiva ?? null,
    trattamentoFineRapporto,
    inail,
    costoAzienda,
  };
}
